import React, { useCallback } from 'react';
import Icon from '@amzn/awsui-components-console/icon';
import { SortOrder } from 'src/generated/graphql';
import { type SortField, type SortState } from '../sortState';
import styles from './SortableColumnHeader.module.scss';

interface SortableColumnHeaderProps {
  readonly label: string;
  readonly field: SortField;
  readonly sortState: SortState;
  readonly onToggleSort: (field: SortField) => void;
}

/**
 * A clickable table column header driving the table's single-column sort.
 *
 * Sorting is server-side: activating a header changes the sort state, which
 * refetches from page 1 (see `SearchResultsTable`). The header is rendered as a
 * real `<button>` inside the column definition:
 * - click         → advance this column through the three-state cycle
 * - Enter / Space → the same (a keyboard-activated button reports a click)
 *
 * THREE-STATE CYCLE on the active column: ascending → descending → unsorted.
 * Clicking a different column starts that column ascending. The unsorted step is
 * the only route back to the backend-decided ordering (relevance for a keyword
 * search, recency for a browse) — see `sortState.ts`. Reaching it correctly
 * removes the indicator from every header: no column is sorted, so none should
 * claim to be.
 *
 * Only ONE column can be sorted at a time, so there is no priority ordinal and
 * no Shift modifier. An arrow shows the direction, and the direction plus the
 * NEXT action are spelled out in the button's `aria-label` so neither is conveyed
 * by the glyph alone.
 *
 * ACCESSIBILITY TRADEOFF — no native `aria-sort`:
 * Bypassing Cloudscape's `sortingColumn` / `sortingDescending` props means
 * Cloudscape no longer manages the `<th>`, so it emits no `aria-sort` attribute
 * and we cannot add one: the column definition only controls the header's
 * CONTENTS, not the `<th>` element itself. Assistive tech therefore does not get
 * the built-in column-sort semantics on the cell, and the button's `aria-label`
 * compensates by spelling out the direction plus how to change it.
 *
 * Now that sorting is single-column, `aria-sort` COULD express this state, so
 * moving to Cloudscape's native table sorting (`sortingColumn` +
 * `onSortingChange`, with `sortingComparator` omitted so no client comparator is
 * introduced) is a viable follow-up that would restore the native semantics.
 * That is a larger refactor of the column definitions and is deliberately not
 * bundled with the server-sort change.
 */
const SortableColumnHeader: React.FC<SortableColumnHeaderProps> = ({
  label,
  field,
  sortState,
  onToggleSort,
}) => {
  const active = sortState?.field === field;
  const ascending = active && sortState?.order === SortOrder.Asc;

  const handleClick = useCallback(() => {
    onToggleSort(field);
  }, [field, onToggleSort]);

  const ariaLabel = !active
    ? `${label}, not sorted. Activate to sort.`
    : ascending
      ? `${label}, sorted ascending. Activate to reverse the sort.`
      : `${label}, sorted descending. Activate to clear the sort.`;

  return (
    <button
      type="button"
      className={active ? `${styles.header} ${styles.active}` : styles.header}
      onClick={handleClick}
      aria-label={ariaLabel}
      data-testid={`sort-header-${field}`}
    >
      <span>{label}</span>
      {active && (
        <span className={styles.indicator} aria-hidden="true">
          <Icon
            name={ascending ? 'caret-up-filled' : 'caret-down-filled'}
            size="small"
          />
        </span>
      )}
    </button>
  );
};

export default SortableColumnHeader;
